import { useEffect, useMemo, useRef, useState } from "react";
import {
  checkInScannerTicket,
  checkOutScannerTicket,
  getScannerHistory,
  listScannerEvents,
  searchScannerTickets,
  verifyScannerTicket
} from "../lib/api";
import type { MetricCardItem } from "../components/admin-ui";
import { MetricCardGroup } from "../components/admin-ui";
import WorkspaceSwitcher from "../components/WorkspaceSwitcher";
import type {
  ScannerEvent,
  ScannerHistoryEntry,
  ScannerResultStatus,
  ScannerSearchResult,
  ScannerStats,
  ScannerVerifyResult
} from "../lib/types";
import type { WorkspaceNavigationAccess } from "../lib/workspace-navigation";

type CameraDevice = { id: string; label: string };
type ScannerState = "ready" | "starting" | "scanning" | "validating" | "permission_denied" | "error";
export type ScanFeedback = {
  sequence: number;
  scannedAt?: string;
  isChecking: boolean;
};
type Html5QrcodeInstance = {
  start: (
    cameraIdOrConfig: string,
    configuration: { fps: number; qrbox: { width: number; height: number } },
    onSuccess: (decodedText: string) => void,
    onError: () => void
  ) => Promise<void>;
  stop: () => Promise<void>;
  clear: () => void;
};
type Html5QrcodeConstructor = {
  new (elementId: string, config?: { verbose?: boolean }): Html5QrcodeInstance;
  getCameras: () => Promise<CameraDevice[]>;
};

const qrRegionId = "antix-qr-reader";

function statusTone(status?: ScannerResultStatus): string {
  if (!status) return "";
  if (status === "checked_out" || status === "checked_in" || status === "checked_out_success") return "success";
  if (status === "already_checked_in") return "warning";
  return "danger";
}

function statusText(status?: ScannerResultStatus): string {
  if (status === "checked_out") return "Valid - ready";
  if (status === "checked_in") return "Checked in";
  if (status === "checked_out_success") return "Checked out";
  if (status === "already_checked_in") return "Already checked in";
  if (status === "wrong_event") return "Wrong event";
  if (status === "void") return "Void";
  if (status === "refunded") return "Refunded";
  if (status === "invalid") return "Invalid";
  return "Ready";
}

function formatDate(value?: string): string {
  if (!value) return "none";
  return new Date(value).toLocaleString();
}

function eventLabel(event?: ScannerEvent): string {
  if (!event) return "No event selected";
  return `${event.title}${event.venueName ? ` / ${event.venueName}` : ""}`;
}

export function nextScanFeedback(previous: ScanFeedback, isChecking: boolean, scannedAt = new Date().toISOString()): ScanFeedback {
  return isChecking
    ? { ...previous, isChecking: true }
    : { sequence: previous.sequence + 1, scannedAt, isChecking: false };
}

export function scanRecencyLabel(scannedAt?: string): string {
  if (!scannedAt) return "";
  return `Scanned just now · ${new Date(scannedAt).toLocaleTimeString([], { hour: "numeric", minute: "2-digit", second: "2-digit" })}`;
}

export function isDuplicateDecode(lastDecoded: { value: string; at: number }, decodedText: string, now: number, debounceMs = 3500): boolean {
  return lastDecoded.value === decodedText && now - lastDecoded.at < debounceMs;
}

export function scannerMetricItems(stats?: ScannerStats): MetricCardItem[] {
  return [
    { label: "Valid tickets", value: String(stats?.validTickets ?? 0) },
    { label: "Checked in now", value: String(stats?.checkedInNow ?? 0) },
    { label: "Check-outs", value: String(stats?.checkedOutCount ?? 0) },
    { label: "Invalid scans", value: String(stats?.invalidScanCount ?? 0) }
  ];
}

export default function ScannerPage({ navigate, workspaceNavigationAccess }: { navigate: (path: string) => void; workspaceNavigationAccess: WorkspaceNavigationAccess }) {
  const [events, setEvents] = useState<ScannerEvent[]>([]);
  const [selectedEventId, setSelectedEventId] = useState("");
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<ScannerSearchResult[]>([]);
  const [scanResult, setScanResult] = useState<ScannerVerifyResult>();
  const [scanFeedback, setScanFeedback] = useState<ScanFeedback>({ sequence: 0, isChecking: false });
  const [history, setHistory] = useState<ScannerHistoryEntry[]>([]);
  const [stats, setStats] = useState<ScannerStats>();
  const [state, setState] = useState<ScannerState>("ready");
  const [message, setMessage] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const [isActing, setIsActing] = useState(false);
  const [cameras, setCameras] = useState<CameraDevice[]>([]);
  const [cameraId, setCameraId] = useState("");
  const scannerRef = useRef<Html5QrcodeInstance>();
  const scannerCtorRef = useRef<Html5QrcodeConstructor>();
  const processingRef = useRef(false);
  const lastDecodedRef = useRef<{ value: string; at: number }>({ value: "", at: 0 });

  const selectedEvent = useMemo(() => events.find((event) => event.id === selectedEventId), [events, selectedEventId]);
  const canScan = Boolean(selectedEventId);

  async function refreshHistory(eventId = selectedEventId, ticketCode?: string) {
    if (!eventId) return;
    const next = await getScannerHistory(eventId, ticketCode);
    setHistory(next.history);
    setStats(next.stats);
  }

  useEffect(() => {
    listScannerEvents().then((nextEvents) => {
      setEvents(nextEvents);
      const requestedEventId = new URL(window.location.href).searchParams.get("eventId") ?? "";
      const nextSelected = nextEvents.find((event) => event.id === requestedEventId)?.id ?? nextEvents[0]?.id ?? "";
      setSelectedEventId(nextSelected);
      if (nextSelected) {
        const url = new URL(window.location.href);
        url.searchParams.set("eventId", nextSelected);
        window.history.replaceState({}, "", `${url.pathname}?${url.searchParams.toString()}`);
        void refreshHistory(nextSelected);
      }
    });

    return () => {
      void stopScanner();
    };
  }, []);

  useEffect(() => {
    if (!selectedEventId) return;
    const url = new URL(window.location.href);
    url.searchParams.set("eventId", selectedEventId);
    window.history.replaceState({}, "", `${url.pathname}?${url.searchParams.toString()}`);
    setResults([]);
    setScanResult(undefined);
    setScanFeedback({ sequence: 0, isChecking: false });
    void refreshHistory(selectedEventId);
  }, [selectedEventId]);

  async function stopScanner() {
    const scanner = scannerRef.current;
    if (!scanner) return;
    try {
      await scanner.stop();
    } catch {
      // Scanner may already be stopped by the browser.
    }
    try {
      scanner.clear();
    } catch {
      // Best-effort cleanup only.
    }
    scannerRef.current = undefined;
    setState("ready");
  }

  async function startScanner(cameraOverride?: string) {
    if (!canScan) {
      setMessage("Select an event before starting the camera.");
      return;
    }
    setMessage("");
    setState("starting");
    try {
      if (!scannerCtorRef.current) {
        const module = await import("html5-qrcode");
        scannerCtorRef.current = module.Html5Qrcode as unknown as Html5QrcodeConstructor;
      }
      const Html5Qrcode = scannerCtorRef.current;
      const available = await Html5Qrcode.getCameras();
      const nextCameras = available.map((camera) => ({ id: camera.id, label: camera.label || camera.id }));
      setCameras(nextCameras);
      const preferredCameraId = cameraOverride || cameraId || nextCameras.find((camera) => /back|rear|environment/i.test(camera.label))?.id || nextCameras[0]?.id;
      if (!preferredCameraId) throw new Error("No camera was found.");
      setCameraId(preferredCameraId);

      const scanner = scannerRef.current ?? new Html5Qrcode(qrRegionId, { verbose: false });
      scannerRef.current = scanner;
      await scanner.start(
        preferredCameraId,
        { fps: 8, qrbox: { width: 250, height: 250 } },
        (decodedText) => {
          void handleDecoded(decodedText);
        },
        () => undefined
      );
      setState("scanning");
    } catch (error) {
      setState("permission_denied");
      setMessage(error instanceof Error ? error.message : "Camera permission was denied or unavailable.");
    }
  }

  async function switchCamera(nextCameraId: string) {
    setCameraId(nextCameraId);
    if (state === "scanning") {
      await stopScanner();
      setTimeout(() => {
        void startScanner(nextCameraId);
      }, 150);
    }
  }

  async function handleDecoded(decodedText: string) {
    const now = Date.now();
    if (processingRef.current) return;
    if (isDuplicateDecode(lastDecodedRef.current, decodedText, now)) return;
    lastDecodedRef.current = { value: decodedText, at: now };
    processingRef.current = true;
    setState("validating");
    setScanResult(undefined);
    setScanFeedback((previous) => nextScanFeedback(previous, true));
    try {
      const result = await verifyScannerTicket(decodedText, selectedEventId, "camera");
      setScanResult(result);
      setScanFeedback((previous) => nextScanFeedback(previous, false));
      setMessage(result.message ?? "");
      await refreshHistory(selectedEventId, result.ticketCode);
      if (navigator.vibrate && ["checked_out", "already_checked_in", "wrong_event", "invalid"].includes(result.status)) {
        navigator.vibrate(result.status === "checked_out" ? 60 : [80, 50, 80]);
      }
    } catch (error) {
      setState("error");
      const errorMessage = error instanceof Error ? error.message : "Scanner API error.";
      setScanResult({ status: "invalid", valid: false, ticketCode: decodedText, eventId: selectedEventId, message: errorMessage });
      setScanFeedback((previous) => nextScanFeedback(previous, false));
      setMessage(errorMessage);
    } finally {
      processingRef.current = false;
      setState(scannerRef.current ? "scanning" : "ready");
    }
  }

  async function handleSearch() {
    if (!selectedEventId) {
      setMessage("Select an event before searching.");
      return;
    }
    setIsSearching(true);
    setMessage("");
    try {
      setResults(await searchScannerTickets(query, selectedEventId));
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Search failed.");
    } finally {
      setIsSearching(false);
    }
  }

  async function loadSearchResult(result: ScannerSearchResult) {
    setMessage("");
    setScanResult(undefined);
    setScanFeedback((previous) => nextScanFeedback(previous, true));
    try {
      const eventId = selectedEventId || result.eventId;
      setScanResult(await verifyScannerTicket(result.ticketCode, eventId, "manual"));
      setScanFeedback((previous) => nextScanFeedback(previous, false));
      await refreshHistory(eventId, result.ticketCode);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "Ticket verification failed.";
      setScanResult({ status: "invalid", valid: false, ticketCode: result.ticketCode, eventId: selectedEventId || result.eventId, message: errorMessage });
      setScanFeedback((previous) => nextScanFeedback(previous, false));
      setMessage(errorMessage);
    }
  }

  async function runAction(action: "check-in" | "check-out") {
    if (!scanResult?.ticketCode || !selectedEventId) return;
    setIsActing(true);
    setMessage("");
    try {
      const response =
        action === "check-in"
          ? await checkInScannerTicket(scanResult.ticketCode, selectedEventId, "manual")
          : await checkOutScannerTicket(scanResult.ticketCode, selectedEventId, "manual");
      setScanResult(response.result);
      setStats(response.stats);
      setHistory(response.stats.recentActivity);
      setMessage(response.result.message ?? "");
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Action failed.");
    } finally {
      setIsActing(false);
    }
  }

  return (
    <section className="page scanner-page">
      <div className="page-title-row">
        <div>
          <div className="eyebrow">Door operations</div>
          <h1>Live scanner</h1>
        </div>
        <div className="scanner-page__header-actions">
          <WorkspaceSwitcher path="/scanner" access={workspaceNavigationAccess} navigate={navigate} />
          <span className={`pill ${state === "scanning" ? "success" : ""}`}>{state.replace("_", " ")}</span>
        </div>
      </div>

      <section className="panel scanner-toolbar">
        <label>
          Event
          <select value={selectedEventId} onChange={(event) => setSelectedEventId(event.target.value)}>
            {events.length === 0 && <option value="">No scanner events</option>}
            {events.map((event) => (
              <option value={event.id} key={event.id}>
                {eventLabel(event)}
              </option>
            ))}
          </select>
        </label>
        <div className="button-row">
          <button className="primary-action" type="button" disabled={!canScan || state === "starting" || state === "scanning"} onClick={() => void startScanner()}>
            {state === "starting" ? "Starting..." : "Start scanner"}
          </button>
          <button className="secondary-action" type="button" disabled={!scannerRef.current} onClick={() => void stopScanner()}>
            Stop
          </button>
        </div>
        {cameras.length > 1 && (
          <label>
            Camera
            <select value={cameraId} onChange={(event) => void switchCamera(event.target.value)}>
              {cameras.map((camera) => (
                <option value={camera.id} key={camera.id}>
                  {camera.label}
                </option>
              ))}
            </select>
          </label>
        )}
      </section>

      <MetricCardGroup items={scannerMetricItems(stats)} className="scanner-stats" />

      {message && <p className="form-error">{message}</p>}

      <div className="scanner-grid">
        <section className="scanner-window">
          <div id={qrRegionId} className="qr-reader" />
          <div className="scan-frame">
            <div className="scan-line" />
            <span>{state === "scanning" ? "Point camera at AnTix QR" : "Camera starts on demand"}</span>
          </div>
        </section>

        <aside className="result-panel">
          <label>
            Manual search
            <input
              placeholder="Ticket code, name, email, order ID"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter") void handleSearch();
              }}
            />
          </label>
          <button className="secondary-action" type="button" onClick={handleSearch} disabled={isSearching || !selectedEventId}>
            {isSearching ? "Searching..." : "Search"}
          </button>

          <ResultCard
            key={scanFeedback.sequence}
            result={scanResult}
            feedback={scanFeedback}
            isActing={isActing}
            onCheckIn={() => void runAction("check-in")}
            onCheckOut={() => void runAction("check-out")}
          />

          {results.length > 0 && (
            <div className="stack-list">
              {results.map((result) => (
                <button className="scan-search-result" type="button" key={result.ticketId} onClick={() => void loadSearchResult(result)}>
                  <strong>{result.ticketCode}</strong>
                  <span>{result.buyerName ?? result.buyerEmail ?? result.orderId ?? "Ticket"}</span>
                  <span>{result.checkedIn ? "checked in" : result.status}</span>
                </button>
              ))}
            </div>
          )}
        </aside>
      </div>

      <section className="panel">
        <div className="panel-header">
          <h2>Recent scan activity</h2>
          <span>{history.length}</span>
        </div>
        <div className="stack-list">
          {history.length === 0 && <p>No scan history for this event yet.</p>}
          {history.map((entry) => (
            <article className="mini-card" key={entry.id}>
              <strong>{statusText(entry.result)}</strong>
              <span>{entry.action} / {entry.source ?? "manual"} / {formatDate(entry.createdAt)}</span>
              <p>{entry.ticketCodeSnapshot ?? entry.scannedCode ?? "No code"} {entry.actorName ? `/ ${entry.actorName}` : ""}</p>
            </article>
          ))}
        </div>
      </section>
    </section>
  );
}

export function ResultCard({
  result,
  feedback,
  isActing,
  onCheckIn,
  onCheckOut
}: {
  result?: ScannerVerifyResult;
  feedback: ScanFeedback;
  isActing: boolean;
  onCheckIn: () => void;
  onCheckOut: () => void;
}) {
  if (feedback.isChecking) {
    return (
      <div className="scan-result-card scan-result-checking" aria-busy="true">
        <span className="pill">Checking ticket...</span>
        <h2>Processing new scan</h2>
        <p>Please wait for the latest ticket result.</p>
      </div>
    );
  }

  if (!result) {
    return (
      <div className="scan-result-card">
        <span className="pill">Ready</span>
        <h2>No ticket selected</h2>
        <p>Scan a QR code or search manually to load a ticket.</p>
      </div>
    );
  }

  const tone = statusTone(result.status);
  const canCheckIn = result.status === "checked_out";
  const canCheckOut = result.status === "already_checked_in" || result.status === "checked_in";

  return (
    <div
      className={`scan-result-card scan-result-${tone} scan-result-fresh`}
      data-scan-sequence={feedback.sequence}
      aria-live="polite"
      aria-atomic="true"
    >
      <span className={`pill ${tone}`}>{statusText(result.status)}</span>
      <h2>{result.ticketTierName ?? "Ticket"}</h2>
      <p>{result.message}</p>
      <p className="scan-result-recency">{scanRecencyLabel(feedback.scannedAt)}</p>
      <div className="ticket-info-grid">
        <span>Event</span>
        <strong>{result.eventTitle ?? result.eventId}</strong>
        <span>Buyer</span>
        <strong>{result.buyerName ?? "Buyer"} {result.buyerEmail ? `/ ${result.buyerEmail}` : ""}</strong>
        <span>Code</span>
        <strong>{result.ticketCode}</strong>
        <span>Last in</span>
        <strong>{formatDate(result.lastCheckedInAt ?? result.checkedInAt)}</strong>
        <span>Last out</span>
        <strong>{formatDate(result.lastCheckedOutAt)}</strong>
      </div>
      <div className="button-row">
        <button className="primary-action" type="button" disabled={!canCheckIn || isActing} onClick={onCheckIn}>
          Check in
        </button>
        <button className="secondary-action" type="button" disabled={!canCheckOut || isActing} onClick={onCheckOut}>
          Check out
        </button>
      </div>
    </div>
  );
}
